import { ActionStepStatus } from '@doss/prisma/types'
import { validate, version } from 'uuid'
import { InputMappingSource } from './types/input-mappings.js'

export const inactiveStatuses: ActionStepStatus[] = [
  ActionStepStatus.INACTIVE,
  ActionStepStatus.FORCED_INACTIVE,
]

/** Prefix to use for all mapped action step inputs to identify them in the backend. */
export const ACTION_STEP_INPUT_PREFIX = '$$DOSS_ACTIONS_STEP_INPUT'

/** Regular expression to match input mapping paths. */
export const INPUT_MAPPING_PATH_REGEX = /{{\$\$DOSS_ACTIONS_STEP_INPUT\.[^}]+}}/g

/** Regular expression to match an input mapping path token outside Handlebars expressions. */
const INPUT_MAPPING_PATH_TOKEN_REGEX = /\$\$DOSS_ACTIONS_STEP_INPUT(?:\.[A-Za-z0-9_-]+)+/g

/**
 * Gets input mapping paths that appear as plain text outside Handlebars expressions.
 *
 * Block-helper bodies may contain authored mapping paths without their own `{{ }}` wrapper. Those
 * paths need to be resolved separately because Handlebars otherwise renders them verbatim.
 */
export function getBareInputMappingPaths(template: string): string[] {
  const paths: string[] = []
  let previousExpressionEnd = 0
  let hasHandlebarsExpression = false
  let expressionStart = template.indexOf('{{')

  while (expressionStart !== -1) {
    const expressionEnd = template.indexOf('}}', expressionStart + 2)
    if (expressionEnd === -1) {
      break
    }

    hasHandlebarsExpression = true
    paths.push(
      ...(template
        .slice(previousExpressionEnd, expressionStart)
        .match(INPUT_MAPPING_PATH_TOKEN_REGEX) ?? []),
    )
    previousExpressionEnd = expressionEnd + 2
    expressionStart = template.indexOf('{{', previousExpressionEnd)
  }

  if (!hasHandlebarsExpression) {
    return []
  }

  paths.push(...(template.slice(previousExpressionEnd).match(INPUT_MAPPING_PATH_TOKEN_REGEX) ?? []))
  return paths
}

/**
 * Gets input mapping from variable
 */
export function getInputMappingFromVariable(variable: string): string {
  return variable.replace('{{', '').replace('}}', '')
}

/**
 * Indicates the type of input for an action step
 *
 * - `USER_INPUT` - The user must provide the input
 * - `MAPPABLE` - The user can provide the input or map it to a input mapping
 * - `MAPPABLE_USER_INPUT` - The user must provide the input and can map it to an input mapping
 *
 * We use this to populate the $comment field of the JSONSchema form widgets to indicate
 * whether the user must provide the input or can map it to an input mapping.
 */
export enum ActionStepInputType {
  USER_INPUT = 'USER_INPUT',
  MAPPABLE = 'MAPPABLE',
  MAPPABLE_USER_INPUT = 'MAPPABLE_USER_INPUT',
  SET_TO_NULL = 'SET_TO_NULL',
}

/**
 * Gets all input mapping variables from a given string value.
 */
export function getInputMappingVariables(value: string): string[] {
  return Array.from(value.matchAll(INPUT_MAPPING_PATH_REGEX), (m) => m[0])
}

/**
 * Creates a mapped action step input path for the given source and path components.
 */
export function createInputMappingPath(
  pathComponents: Array<unknown>,
  source?: InputMappingSource,
): string {
  const prefix: Array<unknown> = [ACTION_STEP_INPUT_PREFIX]
  if (source) {
    prefix.push(source)
  }
  return [...prefix, ...pathComponents].join('.')
}

/**
 * Given a string handles 3 cases:
 * 1. If it is a input mapping, parses it for any IDs
 * 2. If it is just a string, checks if its an ID and returns if true
 * 3. else returns nothing
 */
/**
 * Checks that the string is a valid uuidv4 or uuidv5 (used in tests).
 */
export const isValidActionStepUuid = (id: string): boolean =>
  validate(id) && (version(id) === 4 || version(id) === 5)

export function extractIdsFromInputMappingPath(path: string): string[] {
  const pathNoHandlebars = path.match(/{{(.*?)}}/)?.[1] ?? path

  if (pathNoHandlebars.startsWith(ACTION_STEP_INPUT_PREFIX)) {
    // Parses input mapping for any IDs
    const parts = pathNoHandlebars.split('.')
    const ids = parts.filter((part) => isValidActionStepUuid(part))
    return ids
  } else if (isValidActionStepUuid(pathNoHandlebars)) {
    // If string is just a valid uuid, return it
    return [pathNoHandlebars]
  }
  return []
}
